import { create } from "zustand";
import { DEFAULT_PLATFORM_THEME } from "@playbookai/types";
import { readThemeCookie, writeThemeCookie } from "../settings/theme-cookie.js";
import type { PresentationTheme } from "../settings/types.js";

/**
 * In-memory projection of the durable presentation theme. Playbook BE is the
 * authority; this store owns only the immediate DOM side-effect used by the
 * data-blind shell components, and its one browser copy: every theme
 * `setTheme` applies is mirrored to the shared `pb_theme` cookie, which the
 * Keycloak sign-in page reads (ADR-0092, 2026-10-06 amendment). That covers
 * the setting loading, Settings, the top-bar toggle and a failed save going
 * back, because all of them go through `setTheme`. At start the store opens in
 * the cookie's theme, without writing it, until the saved setting arrives.
 *
 * Every look is an explicit class on `<html>`, exactly one at a time: `light`
 * adds `.pb-light`, `dark` adds `.pb-theme-dark`, `high-contrast` adds
 * `.pb-high-contrast` (PB-5134 d17; light path d21). An explicit class wins
 * over the device, so a saved Dark stays dark on a device that asks for more
 * contrast. `system` (Match device, the default) sets the class of the look it
 * resolves to: more contrast gives high contrast, else a dark device gives
 * dark, else light; it follows both device checks while it is the theme.
 * `darkMode` is the boolean view the TopBar toggle consumes, read from the
 * RESOLVED look: everything that is not the light palette counts as dark.
 */

export interface ProfileState {
  readonly theme: PresentationTheme;
  readonly darkMode: boolean;
  setTheme(theme: PresentationTheme): void;
  setDarkMode(value: boolean): void;
  toggleDarkMode(): void;
}

type ResolvedTheme = Exclude<PresentationTheme, "system">;

const THEME_CLASSES: Record<ResolvedTheme, string> = {
  dark: "pb-theme-dark",
  light: "pb-light",
  "high-contrast": "pb-high-contrast",
};

const MORE_CONTRAST = "(prefers-contrast: more)";
const DARK_DEVICE = "(prefers-color-scheme: dark)";

/* Read when needed, never cached, and guarded like `useMediaQuery`: no
   matchMedia (SSR, a bare jsdom) answers false. */
function deviceMatches(query: string): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(query).matches;
}

export function resolveTheme(theme: PresentationTheme): ResolvedTheme {
  if (theme !== "system") return theme;
  if (deviceMatches(MORE_CONTRAST)) return "high-contrast";
  return deviceMatches(DARK_DEVICE) ? "dark" : "light";
}

export function isDarkTheme(theme: PresentationTheme): boolean {
  return resolveTheme(theme) !== "light";
}

function applyThemeClass(theme: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  const classes = document.documentElement.classList;
  for (const [name, className] of Object.entries(THEME_CLASSES)) {
    classes.toggle(className, name === theme);
  }
}

/* Subscribes to both device checks; returns the unsubscribe. */
function watchDevice(onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const lists = [MORE_CONTRAST, DARK_DEVICE].map((query) =>
    window.matchMedia(query),
  );
  for (const list of lists) list.addEventListener("change", onChange);
  return () => {
    for (const list of lists) list.removeEventListener("change", onChange);
  };
}

let stopWatchingDevice: () => void = () => {};
/* Applies a theme without mirroring it: only the start-up read uses it. */
let follow: (theme: PresentationTheme) => void = () => {};

export const useProfile = create<ProfileState>((set, get) => {
  const project = (theme: PresentationTheme): void => {
    const resolved = resolveTheme(theme);
    set({ theme, darkMode: resolved !== "light" });
    applyThemeClass(resolved);
  };
  follow = (theme) => {
    stopWatchingDevice();
    stopWatchingDevice =
      theme === "system"
        ? watchDevice(() => {
            if (get().theme === "system") project("system");
          })
        : () => {};
    project(theme);
  };
  return {
    theme: DEFAULT_PLATFORM_THEME,
    darkMode: isDarkTheme(DEFAULT_PLATFORM_THEME),
    setTheme(theme) {
      follow(theme);
      writeThemeCookie(theme);
    },
    setDarkMode(value) {
      get().setTheme(value ? "dark" : "light");
    },
    toggleDarkMode() {
      // From the resolved look: high contrast and a dark device go to light,
      // a light device goes to an explicit dark (d29 companion rule).
      get().setTheme(get().darkMode ? "light" : "dark");
    },
  };
});

// Start in the theme last on screen, here or on the sign-in page; write nothing.
follow(readThemeCookie() ?? DEFAULT_PLATFORM_THEME);
